/**
 * Done. / ChiroCPD — Export Page (/export) — CRITICAL PATH
 *
 * Board portal copy-paste export. 8 fields in sacred field_order sequence.
 * ExportSummary dot mini-map + count.
 * Stub data mode: page-level role="alert" banner.
 * CopyFieldProvider wraps all fields for cross-field copy state tracking.
 *
 * Design spec: solid border = complete | dashed border = partial | no border = empty
 * field_order is SACRED — rendered in exact Board portal sequence.
 */

import { AppLayout } from '@/layouts/AppLayout';
import { ExportField } from '@/components/features/ExportField';
import { ExportSummary } from '@/components/features/ExportSummary';
import { Button } from '@/components/ui/Button';
import { useExportFields } from '@/hooks/useExportFields';
import { Link } from 'wouter';

function ExportContent(): React.ReactElement {
  const { fields, stubData, loading, error, retry } = useExportFields();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg
          className="h-8 w-8 animate-spin text-[var(--color-done-500)]"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading export fields…"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-[var(--radius-md)] bg-[var(--color-error-100)] px-4 py-3 text-sm text-red-700"
        role="alert"
      >
        {error}
        <Button variant="ghost" size="sm" onClick={retry} className="ml-3">
          Retry
        </Button>
      </div>
    );
  }

  // Sort fields by fieldOrder (SACRED — must match Board portal)
  const sortedFields = [...fields].sort((a, b) => a.fieldOrder - b.fieldOrder);

  return (
    <div className="flex flex-col gap-6">
      {/* Page heading */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight">
            Export to Board Portal
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Copy each field individually into the{' '}
            <abbr title="Te Poari Kaikorohiti o Aotearoa — Chiropractic Board of New Zealand">
              NZCB
            </abbr>{' '}
            online portal. Fields appear in portal order.
          </p>
        </div>
        <Link href="/activities">
          <Button variant="primary" size="sm">
            + Log Activity
          </Button>
        </Link>
      </div>

      {/* Stub data mode banner */}
      {stubData && (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-[var(--radius-md)] border border-[var(--color-cpd-amber)] bg-[var(--color-warning-100)] px-4 py-3 text-sm text-amber-800"
        >
          <strong>[Sample data — for field validation only]</strong>{' '}
          Log activities to populate real content for copy-paste.{' '}
          <Link href="/activities" className="font-medium underline hover:no-underline">
            Log an activity →
          </Link>
        </div>
      )}

      {/* Export summary + dot mini-map */}
      {fields.length > 0 && (
        <section
          className="rounded-[var(--radius-md)] bg-[var(--surface-base)] px-5 py-4 shadow-sm"
          aria-label="Export readiness summary"
        >
          <ExportSummary fields={sortedFields} />
        </section>
      )}

      {/* Empty state — no activities logged yet */}
      {fields.length === 0 && (
        <div className="rounded-[var(--radius-md)] bg-[var(--surface-muted)] px-4 py-8 text-center">
          <p className="text-sm text-[var(--text-secondary)]">
            No activities logged yet. Log at least one CPD activity to see export fields.
          </p>
          <Link href="/activities" className="mt-3 inline-block text-sm font-medium text-[var(--color-done-600)] hover:underline">
            Log your first activity →
          </Link>
        </div>
      )}

      {/* Field list — in sacred Board portal order */}
      {sortedFields.length > 0 && (
        <section aria-label="Board portal export fields" className="flex flex-col gap-0">
          {sortedFields.map((field, index) => (
            <div
              key={field.fieldName}
              className="rounded-[var(--radius-md)] bg-[var(--surface-base)] px-5 py-5 shadow-sm"
              style={{ marginTop: index > 0 ? '1px' : 0 }}
            >
              <ExportField field={field} />
            </div>
          ))}
        </section>
      )}

      {/* Board portal instructions */}
      <section
        aria-label="Board portal instructions"
        className="rounded-[var(--radius-md)] border border-[var(--border-base)] bg-[var(--surface-subtle)] px-5 py-4 text-sm text-[var(--text-secondary)]"
      >
        <h2 className="font-semibold text-[var(--text-primary)] mb-2 text-sm">
          How to use this page
        </h2>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Open the <strong lang="mi">Te Poari Kaikorohiti o Aotearoa</strong> online portal in another tab.</li>
          <li>Click <strong>Copy to Board</strong> next to each field.</li>
          <li>Paste into the matching field in the portal.</li>
          <li>Repeat for all 8 fields, in order.</li>
          <li>Review all content before submitting to the Board.</li>
        </ol>
        <p className="mt-3 text-xs text-[var(--text-muted)]">
          ChiroCPD is not affiliated with or endorsed by{' '}
          <span lang="mi">Te Poari Kaikorohiti o Aotearoa</span>.
          Always verify field content before submission.
        </p>
      </section>
    </div>
  );
}

export function ExportPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/export">
      <ExportContent />
    </AppLayout>
  );
}
